import React, { useEffect } from "react";

export const StoryNavigationContext = React.createContext(null);

export const withStoryNavigation = (WrappedComponent) => {
  const WithStoryNavigationWrapper = (componentProps) => {
    const [storyIndex, setStoryIndex] = React.useState(0);
    const [storiesLength, setStoriesLength] = React.useState(0);
    const [isStoryLoading, setIsStoryLoading] = React.useState(false);
    const [hasPresentedAllStories, setHasPresentedAllStories] =
      React.useState(false);

    const contextValue = React.useMemo(
      () => ({
        hasPresentedAllStories,
        storyIndex,
        setStoryIndex,
        storiesLength,
        setStoriesLength,
        isStoryLoading,
        setIsStoryLoading,
        setHasPresentedAllStories,
      }),
      [storyIndex, storiesLength, isStoryLoading, hasPresentedAllStories]
    );

    return (
      <StoryNavigationContext.Provider value={contextValue}>
        <WrappedComponent {...componentProps} />
      </StoryNavigationContext.Provider>
    );
  };

  WithStoryNavigationWrapper.displayName = "WithStoryNavigation";

  return WithStoryNavigationWrapper;
};

const getNavigationHandlers = ({
  currentIndex,
  hasPresentedAllStories,
  limit,
  setHasPresentedAllStories,
  setStoryIndex,
}) => {
  const handleBackClick = (event) => {
    event.stopPropagation();

    if (hasPresentedAllStories) {
      setHasPresentedAllStories(false);
      onStoryIndexUpdateBy(0)(currentIndex);
    } else {
      onStoryIndexUpdateBy(-1)(currentIndex);
    }
  };

  const handleNextClick = (event) => {
    event.stopPropagation();
    onStoryIndexUpdateBy(1)(currentIndex);
  };

  const onStoryIndexUpdateBy = (increment) => (currentIndex) => {
    const newIndex = currentIndex + increment;
    if (newIndex === limit || newIndex < 0) return;
    setStoryIndex(newIndex);
  };

  const resetStoryIndex = () => setStoryIndex(0);

  return {
    handleBackClick,
    handleNextClick,
    onStoryIndexUpdateBy,
    resetStoryIndex,
  };
};

export const useStoryNavigation = (initialStoriesLength = 0) => {
  const context = React.useContext(StoryNavigationContext);

  if (!context) {
    throw new Error("This component must be wrapped in withStoryNavigation.");
  }

  const {
    hasPresentedAllStories,
    setHasPresentedAllStories,
    setStoriesLength,
    setStoryIndex,
    storiesLength,
    isStoryLoading,
    setIsStoryLoading,
    storyIndex,
  } = context;

  useEffect(() => {
    if (initialStoriesLength > 0) {
      setStoriesLength(initialStoriesLength);
    }
  }, [initialStoriesLength, setStoriesLength]);

  const navigationHandlers = getNavigationHandlers({
    currentIndex: storyIndex,
    hasPresentedAllStories,
    limit: storiesLength,
    setHasPresentedAllStories,
    setStoryIndex,
  });

  return {
    hasPresentedAllStories,
    setHasPresentedAllStories,
    setStoryIndex,
    storiesLength,
    storyIndex,
    isStoryLoading,
    setIsStoryLoading,
    ...navigationHandlers,
  };
};
